import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { CreditCard, Plus, Edit, Trash2, Eye, DollarSign, Calendar, Users, CheckCircle, XCircle } from 'lucide-react'

interface Plano {
  id: string
  nome: string
  descricao: string
  valor: number
  duracao_dias: number
  max_usuarios: number
  recursos: string[]
  ativo: boolean
  created_at: string
}

const PlansPage = () => {
  const [plans] = useState<Plano[]>([
    {
      id: '1',
      nome: 'Plano Básico',
      descricao: 'Ideal para pequenas empresas',
      valor: 29.90,
      duracao_dias: 30,
      max_usuarios: 5,
      recursos: ['Gestão financeira', 'Relatórios básicos', 'Suporte por email'],
      ativo: true,
      created_at: '2025-01-01'
    },
    {
      id: '2',
      nome: 'Plano Profissional',
      descricao: 'Para empresas em crescimento',
      valor: 59.90,
      duracao_dias: 30,
      max_usuarios: 20,
      recursos: ['Gestão financeira', 'Relatórios avançados', 'Suporte prioritário', 'Integração WhatsApp'],
      ativo: true,
      created_at: '2025-01-01'
    },
    {
      id: '3',
      nome: 'Plano Enterprise',
      descricao: 'Solução completa para grandes empresas',
      valor: 99.90,
      duracao_dias: 30,
      max_usuarios: 100,
      recursos: ['Gestão financeira', 'Relatórios personalizados', 'Suporte 24/7', 'Integração WhatsApp', 'API personalizada'],
      ativo: false,
      created_at: '2025-01-01'
    }
  ])

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  const getStatusBadge = (ativo: boolean) => {
    if (ativo) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3 mr-1" />
          Ativo
        </span>
      )
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        <XCircle className="w-3 h-3 mr-1" />
        Inativo
      </span>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Planos</h1>
          <p className="text-gray-600 mt-2">
            Gerencie os planos e assinaturas do sistema ({plans.length} planos)
          </p>
        </div>
        <button className="admin-button admin-button-primary flex items-center space-x-2">
          <Plus className="w-5 h-5" />
          <span>Novo Plano</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="admin-card text-center">
          <div className="text-2xl font-bold text-blue-600">{plans.length}</div>
          <div className="text-sm text-gray-600">Total de Planos</div>
        </div>
        <div className="admin-card text-center">
          <div className="text-2xl font-bold text-green-600">{plans.filter(p => p.ativo).length}</div>
          <div className="text-sm text-gray-600">Planos Ativos</div>
        </div>
        <div className="admin-card text-center">
          <div className="text-2xl font-bold text-purple-600">
            {formatCurrency(plans.reduce((sum, p) => sum + p.valor, 0))}
          </div>
          <div className="text-sm text-gray-600">Valor Total</div>
        </div>
        <div className="admin-card text-center">
          <div className="text-2xl font-bold text-orange-600">
            {plans.reduce((sum, p) => sum + p.max_usuarios, 0)}
          </div>
          <div className="text-sm text-gray-600">Usuários Máximos</div>
        </div>
      </div>

      {/* Plans List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="admin-card"
      >
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plano
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Preço
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duração
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usuários
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {plans.map((plan) => (
                <tr key={plan.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-admin-100 rounded-lg flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-admin-600" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{plan.nome}</div>
                        <div className="text-sm text-gray-500">{plan.descricao}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{formatCurrency(plan.valor)}</div>
                    <div className="text-xs text-gray-500">por mês</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-gray-900">
                      <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                      {plan.duracao_dias} dias
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-gray-900">
                      <Users className="w-4 h-4 mr-2 text-gray-400" />
                      {plan.max_usuarios} usuários
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(plan.ativo)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button className="text-admin-600 hover:text-admin-900 p-1">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="text-blue-600 hover:text-blue-900 p-1">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button className="text-red-600 hover:text-red-900 p-1">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Resources Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="admin-card"
        >
          <h3 className="text-lg font-medium text-gray-900 mb-4">Recursos dos Planos</h3>
          <div className="space-y-3">
            {plans.map((plan) => (
              <div key={plan.id} className="border-l-4 border-admin-200 pl-4">
                <h4 className="font-medium text-gray-900">{plan.nome}</h4>
                <ul className="mt-2 space-y-1">
                  {plan.recursos.map((recurso, index) => (
                    <li key={index} className="text-sm text-gray-600 flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      {recurso}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="admin-card"
        >
          <h3 className="text-lg font-medium text-gray-900 mb-4">Resumo Financeiro</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Receita Mensal Total:</span>
              <span className="font-medium text-gray-900">
                {formatCurrency(plans.filter(p => p.ativo).reduce((sum, p) => sum + p.valor, 0))}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Planos Ativos:</span>
              <span className="font-medium text-gray-900">{plans.filter(p => p.ativo).length}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Planos Inativos:</span>
              <span className="font-medium text-gray-900">{plans.filter(p => !p.ativo).length}</span>
            </div>
            <div className="pt-4 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <span className="text-lg font-medium text-gray-900">Total de Usuários Suportados:</span>
                <span className="text-lg font-bold text-admin-600">
                  {plans.reduce((sum, p) => sum + p.max_usuarios, 0)}
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default PlansPage
